import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import { memo } from "react";
import dayjs from "dayjs";
import Heatmap from "highcharts/modules/heatmap";

NoDataToDisplay(Highcharts);
Heatmap(Highcharts);

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const convertToCalendarData = (dynamicData) => {
  if (!dynamicData?.length) return [];

  const startDate = new Date(dynamicData[0].from);
  const year = startDate.getFullYear();
  const month = startDate.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  const result = [];
  let current = new Date(firstOfMonth);

  while (current <= lastOfMonth) {
    const currentTime = current.getTime();
    // const dateStr = current.toISOString().split("T")[0];
    const dateStr = dayjs(current).format("YYYY-MM-DD")

    const match = dynamicData.find(
      (entry) => currentTime >= entry.from && currentTime < entry.to
    );

    result.push({
      date: dateStr,
      dataPercentage: match ? match.dataPercentage : null,
    });
    current.setDate(current.getDate() + 1);
  }

  return result;
};

const generateChartData = (data) => {
  const chartData = [];

  const firstDate = new Date(data[0].date);
  const firstWeekday = firstDate.getDay();

  const dayCells = data.map((entry) => {
    const dateObj = new Date(entry.date);
    const dayOfWeek = dateObj.getDay();
    const dayDiff = Math.floor((dateObj - firstDate) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor((firstWeekday + dayDiff) / 7);

    return {
      x: dayOfWeek,
      y: weekNumber,
      value: entry.dataPercentage,
      date: dateObj.getTime(),
      custom: {
        empty: false,
        monthDay: dateObj.getDate(),
      },
    };
  });

  for (let i = 0; i < firstWeekday; i++) {
    chartData.push({
      x: i,
      y: 0,
      value: null,
      date: null,
      custom: { empty: true },
    });
  }

  chartData.push(...dayCells);

  const lastY = chartData[chartData.length - 1].y;
  const lastX = chartData[chartData.length - 1].x;
  const trailing = 6 - lastX;

  for (let i = 1; i <= trailing; i++) {
    chartData.push({
      x: lastX + i,
      y: lastY,
      value: null,
      date: null,
      custom: { empty: true },
    });
  }

  return chartData;
};

const CalendarHeatmapChart = memo(({ data }) => {
  const seriesData = convertToCalendarData(data);
  const chartData = generateChartData(seriesData);

  const options = {
    chart: {
      type: "heatmap",
    },

    title: {
      text: "",
      align: "left",
    },

    accessibility: {
      landmarkVerbosity: "one",
    },

    tooltip: {
      enabled: true,
      outside: true,
      zIndex: 20,
      headerFormat: "",
      // formatter: function () {
      //   if (this.point.custom?.empty) return null;
      //   return Highcharts.dateFormat("%A, %b %e, %Y", this.point.date);
      // },
      nullFormat: "No data",
      backgroundColor: "#fafafa",
      borderWidth: 0,
      padding: 6,
      className: "",
      useHTML: true,
      formatter: function () {
        return this.point.date ? `
          <div class="text-center"> 
            <div class="mb05 bold">
              ${Highcharts.dateFormat("%A, %b %e, %Y", this.point.date)} 
            </div>
            <b>${this.point.value ? this.point.value.toFixed(2) + "%" : "--"}</b>
          </div>`
        : <div>No Data</div>
      }
    },

    xAxis: {
      categories: weekdays,
      opposite: true,
      lineWidth: 30,
      offset: 16,
      lineColor: "#fff",
      labels: {
        rotation: 0,
        y: 20,
        style: {
          textTransform: "uppercase",
          fontWeight: "bold",
        },
      },
      accessibility: {
        description: "weekdays",
      },
    },

    yAxis: {
      reversed: true,
      visible: false,
    },

    legend: {
      align: "right",
      layout: "vertical",
      verticalAlign: "middle",
    },

    colorAxis: {
      min: 0,
      stops: [
        [0.2, "#CCEEFF"],
        // [0.4, "orange"],
        // [0.6, "#ffff4d"],
        [0.9, "#66CCFF"],
      ],
      labels: {
        format: "",
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },

    series: [
      {
        keys: ["x", "y", "value", "date", "id"],
        data: chartData,
        nullColor: "#e9f5f8",
        borderWidth: 2,
        borderColor: "#fff",
        dataLabels: [
          {
            enabled: true,
            formatter: function () {
              if (this.point?.custom?.empty) return "";
              return this.point?.value
                ? `${this.point?.value?.toFixed(2)}%`
                : "--";
            },
            style: {
              textOutline: "none",
              fontWeight: "normal",
              fontSize: "14px",
            },
            y: 4,
          },
          {
            enabled: true,
            align: "left",
            verticalAlign: "top",
            formatter: function () {
              if (this.point?.custom?.empty) return "";
              return `${this.point?.custom?.monthDay}`;
            },
            backgroundColor: "whitesmoke",
            padding: 2,
            style: {
              textOutline: "none",
              color: "rgba(70, 70, 92, 1)",
              fontSize: "0.7rem",
              fontWeight: "bold",
              opacity: 0.5,
            },
            x: 1,
            y: 1,
          },
        ],
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
});

export default CalendarHeatmapChart;
